import type { DataApi } from './types';
import type { Group, Expense, TodoTask, UserProfile, Tab } from '../types';
import {
  getFirestore, collection, addDoc, query, where, getDocs, doc, updateDoc,
  deleteDoc, arrayUnion, serverTimestamp, getDoc, onSnapshot, orderBy, deleteField, setDoc
} from 'firebase/firestore';
import {
  getAuth, signInAnonymously, signInWithEmailAndPassword, signOut,
  linkWithCredential, EmailAuthProvider, onAuthStateChanged, updateProfile, type User,
  createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, getAdditionalUserInfo,
  sendPasswordResetEmail, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';
import type { FirebaseApp } from 'firebase/app';

const GROUPS_COLLECTION = 'groups';
const TABS_COLLECTION = 'tabs';
const EXPENSES_COLLECTION = 'expenses';
const TASKS_COLLECTION = 'tasks';
const USERS_COLLECTION = 'users';

export class FirebaseApi implements DataApi {
  private db;
  private firebaseAuth;
  private profileCache = new Map<string, UserProfile>();

  constructor(app: FirebaseApp) {
    this.db = getFirestore(app);
    this.firebaseAuth = getAuth(app);
  }

  groups = {
    create: async (name: string, currency: string, userId: string): Promise<Group> => {
      // Use ID instead of code
      const groupData = {
        name,
        currency,
        createdBy: userId,
        members: [userId],
        createdAt: serverTimestamp(),
      };
      const docRef = await addDoc(collection(this.db, GROUPS_COLLECTION), groupData);
      return { id: docRef.id, ...groupData } as unknown as Group;
    },
    join: async (groupId: string, userId: string): Promise<Group> => {
      const docRef = doc(this.db, GROUPS_COLLECTION, groupId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Group not found");
      }

      const groupData = docSnap.data() as Omit<Group, 'id'>;

      if (!groupData.members.includes(userId)) {
        await updateDoc(docRef, {
          members: arrayUnion(userId)
        });
        return { id: docSnap.id, ...groupData, members: [...groupData.members, userId] };
      }

      return { id: docSnap.id, ...groupData };
    },
    getUserGroups: async (userId: string): Promise<Group[]> => {
      const q = query(collection(this.db, GROUPS_COLLECTION), where("members", "array-contains", userId));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Group[];
    },
    getById: async (groupId: string): Promise<Group | null> => {
      const docRef = doc(this.db, GROUPS_COLLECTION, groupId);
      const docSnap = await getDoc(docRef);
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as Group : null;
    },
    subscribe: (groupId: string, callback: (group: Group | null) => void): () => void => {
      return onSnapshot(doc(this.db, GROUPS_COLLECTION, groupId), (doc) => {
        callback(doc.exists() ? { id: doc.id, ...doc.data() } as Group : null);
      });
    }
  };

  tabs = {
    create: async (tabData: Omit<Tab, 'id' | 'createdAt'>): Promise<Tab> => {
      // No more code generation
      const docRef = await addDoc(collection(this.db, TABS_COLLECTION), {
        ...tabData,
        manualMembers: [],
        createdAt: serverTimestamp(),
      });
      // Return with ID
      return { id: docRef.id, ...tabData, manualMembers: [] } as unknown as Tab;
    },
    join: async (tabId: string, userId: string): Promise<Tab> => {
      const docRef = doc(this.db, TABS_COLLECTION, tabId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Tab not found");
      }

      const tabData = docSnap.data() as Omit<Tab, 'id'>;

      // Check membership
      if (!tabData.members.includes(userId)) {
        await updateDoc(docRef, {
          members: arrayUnion(userId)
        });
        return { id: docSnap.id, ...tabData, members: [...tabData.members, userId] };
      }

      return { id: docSnap.id, ...tabData };
    },
    update: async (id: string, tabData: Partial<Tab>): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updateData: Record<string, any> = {
        ...tabData,
        updatedAt: serverTimestamp(),
      };
      
      // Remove undefined values to prevent Firebase errors
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      Object.keys(updateData).forEach(key => updateData[key] === undefined && delete updateData[key]);

      // If paymentConfig is explicitly null, delete the field
      if (tabData.paymentConfig === null) {
        updateData.paymentConfig = deleteField();
      }

      await updateDoc(doc(this.db, TABS_COLLECTION, id), updateData);
    },
    delete: async (id: string): Promise<void> => {
      await deleteDoc(doc(this.db, TABS_COLLECTION, id));
    },
    getById: async (id: string): Promise<Tab | null> => {
      const docRef = doc(this.db, TABS_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as Tab : null;
    },
    getUserTabs: async (userId: string): Promise<Tab[]> => {
      const q = query(
        collection(this.db, TABS_COLLECTION),
        where("members", "array-contains", userId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }) as unknown as Tab)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    },
    subscribe: (id: string, callback: (tab: Tab | null) => void): () => void => {
      return onSnapshot(doc(this.db, TABS_COLLECTION, id), (doc) => {
        callback(doc.exists() ? { id: doc.id, ...doc.data() } as Tab : null);
      });
    },
    subscribeUserTabs: (userId: string, callback: (tabs: Tab[]) => void): () => void => {
      const q = query(
        collection(this.db, TABS_COLLECTION),
        where("members", "array-contains", userId)
      );
      return onSnapshot(q, (snapshot) => {
        const tabs = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }) as unknown as Tab)
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        callback(tabs);
      });
    }
  };

  expenses = {
    add: async (expenseData: Omit<Expense, 'id' | 'createdAt'>): Promise<Expense> => {
      const docRef = await addDoc(collection(this.db, EXPENSES_COLLECTION), {
        ...expenseData,
        createdAt: serverTimestamp(),
      });
      return { id: docRef.id, ...expenseData } as unknown as Expense;
    },
    update: async (id: string, expenseData: Partial<Expense>): Promise<void> => {
      await updateDoc(doc(this.db, EXPENSES_COLLECTION, id), expenseData);
    },
    delete: async (id: string): Promise<void> => {
      await deleteDoc(doc(this.db, EXPENSES_COLLECTION, id));
    },
    getGroupExpenses: async (groupId: string): Promise<Expense[]> => {
      const q = query(
        collection(this.db, EXPENSES_COLLECTION),
        where("groupId", "==", groupId),
        orderBy("date", "desc")
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Expense[];
    },
    subscribe: (groupId: string, callback: (expenses: Expense[]) => void): () => void => {
      const q = query(
        collection(this.db, EXPENSES_COLLECTION),
        where("groupId", "==", groupId),
        orderBy("date", "desc")
      );
      return onSnapshot(q, (snapshot) => {
        const expenses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Expense[];
        callback(expenses);
      });
    }
  };

  tasks = {
    add: async (taskData: Omit<TodoTask, 'id' | 'createdAt'>): Promise<TodoTask> => {
      const docRef = await addDoc(collection(this.db, TASKS_COLLECTION), {
        ...taskData,
        createdAt: serverTimestamp(),
      });
      return { id: docRef.id, ...taskData } as unknown as TodoTask;
    },
    toggle: async (taskId: string, completed: boolean): Promise<void> => {
      await updateDoc(doc(this.db, TASKS_COLLECTION, taskId), { completed });
    },
    delete: async (taskId: string): Promise<void> => {
      await deleteDoc(doc(this.db, TASKS_COLLECTION, taskId));
    },
    getGroupTasks: async (groupId: string): Promise<TodoTask[]> => {
      const q = query(collection(this.db, TASKS_COLLECTION), where("groupId", "==", groupId));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as TodoTask[];
    },
    subscribe: (groupId: string, callback: (tasks: TodoTask[]) => void): () => void => {
      const q = query(collection(this.db, TASKS_COLLECTION), where("groupId", "==", groupId));
      return onSnapshot(q, (snapshot) => {
        const tasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as TodoTask[];
        callback(tasks);
      });
    }
  };

  users = {
    get: async (uid: string): Promise<UserProfile | null> => {
      if (this.profileCache.has(uid)) return this.profileCache.get(uid) ?? null;
      const docSnap = await getDoc(doc(this.db, USERS_COLLECTION, uid));
      if (docSnap.exists()) {
        const data = docSnap.data() as UserProfile;
        this.profileCache.set(uid, data);
        return data;
      }
      return null;
    },
    getMultiple: async (uids: string[]): Promise<UserProfile[]> => {
      if (uids.length === 0) return [];

      const allProfiles: UserProfile[] = [];
      const missingUids: string[] = [];
      const foundUids = new Set<string>();

      // 1. Check cache first
      for (const uid of uids) {
        if (this.profileCache.has(uid)) {
          const cached = this.profileCache.get(uid);
          if (cached) allProfiles.push(cached);
        } else {
          missingUids.push(uid);
        }
      }

      if (missingUids.length === 0) return allProfiles;

      // 2. Try fetching missing from public_profiles
      const chunks = [];
      for (let i = 0; i < missingUids.length; i += 30) chunks.push(missingUids.slice(i, i + 30));

      try {
        for (const chunk of chunks) {
          const q = query(collection(this.db, 'public_profiles'), where("uid", "in", chunk));
          const querySnapshot = await getDocs(q);
          querySnapshot.forEach((doc) => {
            const data = doc.data() as UserProfile;
            allProfiles.push(data);
            foundUids.add(data.uid);
            this.profileCache.set(data.uid, data);
          });
        }
      } catch (e) {
        console.warn("Failed to fetch public profiles", e);
      }

      // 3. Identify still missing UIDs
      const stillMissingUids = missingUids.filter(uid => !foundUids.has(uid));

      // 4. Try fetching from 'users' collection (Private)
      if (stillMissingUids.length > 0 && this.firebaseAuth.currentUser) {
        const missingChunks = [];
        for (let i = 0; i < stillMissingUids.length; i += 30) missingChunks.push(stillMissingUids.slice(i, i + 30));

        try {
          for (const chunk of missingChunks) {
            const q = query(collection(this.db, USERS_COLLECTION), where("uid", "in", chunk));
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach((doc) => {
              const data = doc.data() as UserProfile;
              if (!foundUids.has(data.uid)) {
                allProfiles.push(data);
                foundUids.add(data.uid);
                this.profileCache.set(data.uid, data);
              }
            });
          }
        } catch (e) {
          console.warn("Failed to fetch private profiles fallback", e);
        }
      }

      return allProfiles;
    },
    updateDisplayName: async (uid: string, name: string): Promise<void> => {
      const updateData = {
        displayName: name,
        updatedAt: new Date().toISOString()
      };

      // Update privates
      await updateDoc(doc(this.db, USERS_COLLECTION, uid), updateData);

      // Update public
      await setDoc(doc(this.db, 'public_profiles', uid), {
        uid,
        displayName: name,
        photoURL: this.firebaseAuth.currentUser?.photoURL ?? null
      }, { merge: true });

      if (this.firebaseAuth.currentUser?.uid === uid) {
        await updateProfile(this.firebaseAuth.currentUser, { displayName: name });
      }

      if (this.profileCache.has(uid)) {
        const p = this.profileCache.get(uid);
        if (p) this.profileCache.set(uid, { ...p, displayName: name });
      }
    },
    updateProfile: async (uid: string, data: Partial<UserProfile>): Promise<void> => {
      await updateDoc(doc(this.db, USERS_COLLECTION, uid), {
        ...data,
        updatedAt: new Date().toISOString()
      });

      // Sync public fields if present
      if (data.displayName || data.photoURL) {
        const publicData: Partial<UserProfile> = {};
        if (data.displayName) publicData.displayName = data.displayName;
        if (data.photoURL) publicData.photoURL = data.photoURL;
        publicData.uid = uid;
        await setDoc(doc(this.db, 'public_profiles', uid), publicData, { merge: true });
      }

      if (this.profileCache.has(uid)) {
        const p = this.profileCache.get(uid);
        if (p) this.profileCache.set(uid, { ...p, ...data });
      }
    }
  };

  auth = {
    loginAnonymously: async (): Promise<User> => {
      const cred = await signInAnonymously(this.firebaseAuth);
      return cred.user;
    },
    signInWithEmail: async (email: string, password: string): Promise<User> => {
      const cred = await signInWithEmailAndPassword(this.firebaseAuth, email, password);
      return cred.user;
    },
    linkWithEmail: async (email: string, password: string): Promise<User> => {
      if (!this.firebaseAuth.currentUser) throw new Error("No user logged in");
      const cred = EmailAuthProvider.credential(email, password);
      const result = await linkWithCredential(this.firebaseAuth.currentUser, cred);

      // Update Firestore with new email
      await updateDoc(doc(this.db, USERS_COLLECTION, result.user.uid), {
        email: result.user.email,
        updatedAt: new Date().toISOString(),
        isAnonymous: false
      });

      return result.user;
    },
    logout: async (): Promise<void> => {
      await signOut(this.firebaseAuth);
    },
    subscribe: (callback: (user: User | null) => void): () => void => {
      return onAuthStateChanged(this.firebaseAuth, callback);
    },
    signUp: async (email: string, password: string): Promise<User> => {
      const cred = await createUserWithEmailAndPassword(this.firebaseAuth, email, password);
      // Create initial user document
      const user = cred.user;
      await setDoc(doc(this.db, USERS_COLLECTION, user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName ?? `User ${user.uid.substring(0, 4)}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        photoURL: user.photoURL,
        isAnonymous: false
      });

      // Create public profile
      await setDoc(doc(this.db, 'public_profiles', user.uid), {
        uid: user.uid,
        displayName: user.displayName ?? `User ${user.uid.substring(0, 4)}`,
        photoURL: user.photoURL
      });

      return user;
    },
    signInWithGoogle: async (): Promise<User> => {
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(this.firebaseAuth, provider);
      const user = cred.user;
      const additionalInfo = getAdditionalUserInfo(cred);
      
      if (additionalInfo?.isNewUser) {
        // Create initial user document
        await setDoc(doc(this.db, USERS_COLLECTION, user.uid), {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName ?? `User ${user.uid.substring(0, 4)}`,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          photoURL: user.photoURL,
          isAnonymous: false
        });

        // Create public profile
        await setDoc(doc(this.db, 'public_profiles', user.uid), {
          uid: user.uid,
          displayName: user.displayName ?? `User ${user.uid.substring(0, 4)}`,
          photoURL: user.photoURL
        });
      }

      return user;
    },
    sendPasswordResetEmail: async (email: string): Promise<void> => {
      await sendPasswordResetEmail(this.firebaseAuth, email);
    },
    sendMagicLink: async (email: string, redirectUrl: string): Promise<void> => {
      const actionCodeSettings = {
        url: redirectUrl,
        handleCodeInApp: true,
      };
      await sendSignInLinkToEmail(this.firebaseAuth, email, actionCodeSettings);
    },
    isMagicLink: (url: string): boolean => {
      return isSignInWithEmailLink(this.firebaseAuth, url);
    },
    signInWithMagicLink: async (email: string, url: string): Promise<User> => {
      const cred = await signInWithEmailLink(this.firebaseAuth, email, url);
      const user = cred.user;
      const additionalInfo = getAdditionalUserInfo(cred);
      
      if (additionalInfo?.isNewUser) {
        await setDoc(doc(this.db, USERS_COLLECTION, user.uid), {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName ?? `User ${user.uid.substring(0, 4)}`,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          photoURL: user.photoURL,
          isAnonymous: false
        });

        await setDoc(doc(this.db, 'public_profiles', user.uid), {
          uid: user.uid,
          displayName: user.displayName ?? `User ${user.uid.substring(0, 4)}`,
          photoURL: user.photoURL
        });
      }
      return user;
    },
    deleteAccount: async (): Promise<void> => {
      // Call the Gen 2 cloud function which performs Firestore cleanup
      // and deletes the Firebase Auth user via the Admin SDK server-side.
      const deleteAccountFn = httpsCallable(functions, 'deleteAccount');
      await deleteAccountFn({});
    }
  };
}
